import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { getHostTrips } from '../../../services/firestore/trips';
import { getHostBookings } from '../../../services/firestore/bookings';
import { getHostProfile } from '../../../services/firestore/hosts';
import { AlertCircle, User, Plus, ArrowRight } from 'lucide-react';

const HostDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [trips, setTrips] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [hostProfile, setHostProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      // Wait for auth to finish loading and user to be available
      if (authLoading || !user) {
        setLoading(true);
        return;
      }
      
      try {
        setLoading(true);
        const [tripsData, bookingsData, profileData] = await Promise.all([
          getHostTrips(user.uid),
          getHostBookings(user.uid),
          getHostProfile(user.uid),
        ]);
        setTrips(tripsData);
        setBookings(bookingsData);
        setHostProfile(profileData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user, authLoading]);

  // Show loading state while auth is loading or user data is being fetched
  if (authLoading || loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  const pendingBookings = bookings.filter((b) => b.status === 'pending').length;
  const needsProfile = !hostProfile || !hostProfile.bio || !hostProfile.property;
  const needsFirstListing = trips.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">
            Welcome back, {user?.displayName || 'Host'}
          </h1>
          <p className="text-lg text-gray-600 font-light">
            Manage your properties and bookings
          </p>
        </div>

        {/* Notifications Section */}
        {(needsProfile || needsFirstListing) && (
          <div className="mb-8 space-y-4">
            {needsProfile && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-amber-600 mt-0.5 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-amber-900 mb-1">
                      Complete Your Profile
                    </h3>
                    <p className="text-sm text-amber-700 mb-4">
                      Add your property details and photos to help guests discover your space.
                    </p>
                    <Link
                      to="/host/profile"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                    >
                      <User className="w-4 h-4" />
                      Complete Profile
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {needsFirstListing && (
              <div className="bg-emerald-50 border-l-4 border-emerald-400 p-6 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-emerald-600 mt-0.5 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-emerald-900 mb-1">
                      Post Your First Listing
                    </h3>
                    <p className="text-sm text-emerald-700 mb-4">
                      Create your first trip listing to start welcoming guests.
                    </p>
                    <Link
                      to="/host/trips/create"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Create Listing
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/host/trips"
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-6 hover:shadow-xl hover:border-emerald-200 transition-all duration-300 cursor-pointer group"
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2 group-hover:text-emerald-700 transition-colors">Total Listings</h3>
            <p className="text-4xl font-light text-gray-900 group-hover:text-emerald-700 transition-colors">{trips.length}</p>
          </Link>
          <Link
            to="/host/bookings"
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-6 hover:shadow-xl hover:border-emerald-200 transition-all duration-300 cursor-pointer group"
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2 group-hover:text-emerald-700 transition-colors">Total Bookings</h3>
            <p className="text-4xl font-light text-gray-900 group-hover:text-emerald-700 transition-colors">{bookings.length}</p>
          </Link>
          <Link
            to="/host/bookings"
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-6 hover:shadow-xl hover:border-emerald-200 transition-all duration-300 cursor-pointer group"
          >
            <h3 className="text-sm font-medium text-gray-500 mb-2 group-hover:text-amber-700 transition-colors">Pending</h3>
            <p className="text-4xl font-light text-amber-600 group-hover:text-amber-700 transition-colors">{pendingBookings}</p>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link
            to="/host/trips/create"
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-8 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Create New Listing</h3>
                <p className="text-sm text-gray-600 font-light">
                  Add a new listing to your property
                </p>
              </div>
              <div className="p-4 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                <Plus className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </Link>

          <Link
            to="/host/trips"
            className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-8 hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Manage Listings</h3>
                <p className="text-sm text-gray-600 font-light">
                  View and edit your listings
                </p>
              </div>
              <div className="p-4 bg-emerald-100 rounded-lg group-hover:bg-emerald-200 transition-colors">
                <ArrowRight className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </Link>
        </div>

        {/* Recent Bookings Preview */}
        {bookings.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-medium text-gray-900">Recent Bookings</h2>
              <Link
                to="/host/bookings"
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
              >
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {bookings.slice(0, 3).map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {booking.tripId ? 'Trip Booking' : 'Booking'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Status: <span className="capitalize">{booking.status}</span>
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : booking.status === 'confirmed'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {booking.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostDashboard;
